import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtClock, todayStr } from '../lib/format.js';
import { kioskApi } from '../lib/kiosk-api.js';

// Read-only weekly schedule on the kiosk. Defaults to the current ISO
// week; arrows step backward/forward. No assignment editing, no PII —
// shows name + role + time per shift, grouped by day.
export function KioskScheduleWeek({ session }) {
  const locKey = session.location.key;
  const myId = session.user?.square_id || null;
  const [weekStart, setWeekStart] = useState(() => {
    // Monday of the current ISO week.
    const d = new Date();
    const dow = d.getDay();          // 0=Sun
    const off = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + off);
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true); setErr('');
    kioskApi('GET', `/api/kiosk/${locKey}/schedule/week?week_start=${weekStart}`, null, session.token)
      .then(r => setData(r))
      .catch(e => setErr(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [weekStart]);

  function shiftWeek(weeks) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  }

  if (loading && !data) return <div style={{ fontSize: 13, color: '#9e9e9e' }}>Loading schedule…</div>;
  if (err) return <div style={{ background: '#fee', padding: 12, borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>{err}</div>;
  if (!data) return null;

  const todayYmd = todayStr();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => shiftWeek(-1)}
          style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    background: '#fff', border: '1px solid #d0d4d9', borderRadius: 6,
                    cursor: 'pointer', fontFamily: 'inherit' }}>← Prev</button>
        <button onClick={() => shiftWeek(1)}
          style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    background: '#fff', border: '1px solid #d0d4d9', borderRadius: 6,
                    cursor: 'pointer', fontFamily: 'inherit' }}>Next →</button>
        <span style={{ fontSize: 12, color: '#757575' }}>
          Week of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {data.locationName}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.days.map(d => {
          const date = new Date(d.date + 'T12:00:00Z');
          const dayLabel = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()];
          const friendly = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const isToday = d.date === todayYmd;
          // Sort by start time; group by employee within day for tidy rows.
          const sorted = [...d.shifts].sort((a, b) => (a.scheduledStart || '').localeCompare(b.scheduledStart || ''));
          return (
            <div key={d.date} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
                                         padding: 12, borderLeft: isToday ? '4px solid var(--forest, #1B2B1B)' : '1px solid #e0e0e0' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>{dayLabel}</span>
                <span style={{ fontSize: 12, color: '#757575' }}>{friendly}</span>
                {isToday && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
                                  background: '#e8f5e9', color: '#2e7d32', textTransform: 'uppercase' }}>Today</span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9e9e9e' }}>
                  {sorted.length} shift{sorted.length === 1 ? '' : 's'}
                </span>
              </div>
              {sorted.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic' }}>No one scheduled.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {sorted.map((s, i) => {
                    const isMe = myId && s.teamMemberId === myId;
                    return (
                      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                                              padding: '6px 8px', borderRadius: 6,
                                              background: isMe ? '#e3f2fd' : '#fafafa',
                                              border: isMe ? '1px solid #90caf9' : '1px solid #f0f0f0' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#212121', minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}
                        </span>
                        <span style={{ fontSize: 13, color: '#37474f', flex: 1, minWidth: 0,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.employee || 'Open shift'}
                        </span>
                        {isMe && <span style={{ fontSize: 10, fontWeight: 700, color: '#1565c0' }}>YOU</span>}
                        {s.role && <span style={{ fontSize: 10, color: '#9e9e9e' }}>{s.role}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
